import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Save, BarChart3, Timer, AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

const FIELDS = [
  ["home_title", "Accueil — titre", "text"], ["home_description", "Accueil — description", "textarea"],
  ["city_title", "Pages villes — titre  (variables : {ville})", "text"], ["city_description", "Pages villes — description  ({ville})", "textarea"],
  ["store_title", "Pages boutiques — titre  ({boutique}, {ville})", "text"], ["store_description", "Pages boutiques — description  ({boutique}, {ville})", "textarea"],
  ["product_title", "Pages produits — titre  ({produit}, {categorie})", "text"], ["product_description", "Pages produits — description  ({produit}, {categorie}, {extrait})", "textarea"],
];
const LEN_OK = (k, v) => (k.endsWith("_title") ? v.length <= 60 : v.length <= 160);

export function SeoSettingsForm() {
  const [s, setS] = useState(null);
  const [cities, setCities] = useState([]);
  const [city, setCity] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/seo/settings").then((r) => setS(r.data)).catch(console.error);
    api.get("/cities").then((r) => setCities((r.data || []).map((c) => c.name || c))).catch(console.error);
  }, []);
  if (!s) return <p className="text-zinc-500">Chargement…</p>;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/seo/settings", s); setS(data); toast.success("Réglages SEO enregistrés — visibles immédiatement sur le site"); }
    catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };
  const intro = (s.city_intros || {})[city] || "";
  return (
    <div className="bg-ink-surface border border-zinc-800 p-4 space-y-4" data-testid="seo-settings-form">
      <div>
        <h3 className="font-anton text-lg uppercase text-white flex items-center gap-2"><Search className="h-4 w-4 text-neon-green" /> Titres & descriptions (balises meta)</h3>
        <p className="text-zinc-500 text-xs mt-1">Ce que Google affiche dans ses résultats. Conseils : titre ≤ 60 caractères avec le mot-clé + la ville, description ≤ 160 caractères qui donne envie de cliquer (livraison rapide, main propre, légal). Les variables entre accolades sont remplacées automatiquement.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {FIELDS.map(([k, label, type]) => (
          <label key={k} className="block">
            <span className="text-zinc-400 text-[11px] uppercase font-bold">{label}</span>
            {type === "textarea" ? (
              <textarea data-testid={`seo-${k}`} rows={3} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1 w-full bg-ink border border-zinc-700 text-white px-3 py-2 text-sm focus:outline-none focus:border-neon-magenta" />
            ) : (
              <input data-testid={`seo-${k}`} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1 w-full bg-ink border border-zinc-700 text-white px-3 py-2 text-sm focus:outline-none focus:border-neon-magenta" />
            )}
            <span className={`text-[10px] ${LEN_OK(k, s[k] || "") ? "text-zinc-600" : "text-orange-400 font-bold"}`}>{(s[k] || "").length} car. {LEN_OK(k, s[k] || "") ? "" : "— un peu long pour Google"}</span>
          </label>
        ))}
      </div>
      <div className="border-t border-zinc-800 pt-4">
        <h4 className="font-anton uppercase text-white text-sm mb-1">Texte d'intro local par ville</h4>
        <p className="text-zinc-500 text-xs mb-2">Paragraphe affiché sous le titre de la page ville (2-4 phrases : quartiers desservis, délai, ambiance locale, mots-clés « livraison CBD {'{ville}'} »). Vide = texte par défaut.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <select data-testid="seo-intro-city" value={city} onChange={(e) => setCity(e.target.value)} className="bg-ink border border-zinc-700 text-white px-3 py-2 text-sm sm:w-56">
            <option value="">— Choisir une ville —</option>
            {cities.map((c) => <option key={c} value={c}>{c}{(s.city_intros || {})[c] ? " ✎" : ""}</option>)}
          </select>
          <textarea data-testid="seo-intro-text" disabled={!city} rows={4} value={intro} placeholder={city ? `Ex. : À ${city}, nos livreurs couvrent le centre et les quartiers voisins en moins de 2h…` : "Choisis une ville"} onChange={(e) => setS({ ...s, city_intros: { ...(s.city_intros || {}), [city]: e.target.value } })} className="flex-1 bg-ink border border-zinc-700 text-white px-3 py-2 text-sm focus:outline-none focus:border-neon-magenta disabled:opacity-40" />
        </div>
      </div>
      <button data-testid="seo-save" disabled={busy} onClick={save} className="inline-flex items-center gap-2 bg-neon-green text-black font-anton uppercase px-5 py-2.5 hover:bg-white transition-colors disabled:opacity-40"><Save className="h-4 w-4" /> Enregistrer</button>
    </div>
  );
}

const Kpi = ({ label, value, hint, testId }) => (
  <div className="bg-ink border border-zinc-800 p-3" data-testid={testId}>
    <p className="text-zinc-500 text-[10px] uppercase font-bold">{label}</p>
    <p className="font-anton text-2xl text-white leading-none mt-1">{value}</p>
    {hint && <p className="text-zinc-600 text-[10px] mt-1">{hint}</p>}
  </div>
);

const Bars = ({ title, rows, total, testId }) => (
  <div className="bg-ink border border-zinc-800 p-3" data-testid={testId}>
    <p className="text-neon-green text-[11px] uppercase font-bold mb-2">{title}</p>
    {rows.length === 0 ? <p className="text-zinc-600 text-xs">Pas encore de données</p> : rows.map((r) => (
      <div key={r.key} className="mb-1.5">
        <div className="flex justify-between text-xs"><span className="text-zinc-300 truncate">{r.key}</span><span className="text-zinc-500 ml-2">{r.count}</span></div>
        <div className="h-1 bg-zinc-800"><div className="h-full bg-neon-magenta" style={{ width: `${Math.max(3, (100 * r.count) / Math.max(total, 1))}%` }} /></div>
      </div>
    ))}
  </div>
);

export function AudienceDashboard() {
  const [days, setDays] = useState(7);
  const [a, setA] = useState(null);
  const [ov, setOv] = useState(null);
  const load = useCallback(() => {
    api.get(`/admin/analytics?days=${days}`).then((r) => setA(r.data)).catch(console.error);
    api.get("/admin/seo/overview").then((r) => setOv(r.data)).catch(console.error);
  }, [days]);
  useEffect(() => { load(); }, [load]);
  if (!a) return <p className="text-zinc-500">Chargement…</p>;
  const f = a.funnel;
  const steps = [["Visites", f.sessions], ["Boutique vue", f.storefront], ["Produit vu", f.product], ["Ajout panier", f.add_to_cart], ["Checkout", f.checkout], ["Formulaire commencé", f.form_started || 0], ["Clic sur Confirmer", f.confirm_click || 0], ["Commandes", f.orders]];
  const errFields = f.error_fields || [];
  const FIELD_LABEL = { customer_name: "Nom", customer_phone: "Téléphone", customer_address: "Adresse", snap_handle: "Pseudo Snapchat" };
  const maxPv = Math.max(...a.series.map((d) => d.pageviews), 1);
  return (
    <div className="space-y-4" data-testid="audience-dashboard">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-anton text-lg uppercase text-white flex items-center gap-2"><BarChart3 className="h-4 w-4 text-neon-magenta" /> Suivi SEO & comportement client</h3>
        <div className="ml-auto flex gap-1">
          {[7, 30, 90].map((d) => <button key={d} data-testid={`audience-days-${d}`} onClick={() => setDays(d)} className={`text-xs font-bold px-3 py-1.5 border ${days === d ? "bg-neon-green text-black border-neon-green" : "border-zinc-700 text-zinc-400"}`}>{d} j</button>)}
        </div>
      </div>
      {ov && (
        <div className="flex flex-wrap gap-2 text-xs" data-testid="seo-overview">
          <span className="border border-zinc-800 px-2 py-1 text-zinc-400">Pages indexables : <b className="text-white">{ov.indexable.cities}</b> villes · <b className="text-white">{ov.indexable.products}</b> produits · <b className="text-white">{ov.indexable.blog_posts}</b> articles</span>
          <span className="border border-zinc-800 px-2 py-1 text-zinc-400">Intros villes personnalisées : <b className="text-white">{ov.intros_customized}</b></span>
          <a href={ov.sitemap_url} target="_blank" rel="noopener noreferrer" className="border border-zinc-800 px-2 py-1 text-neon-green inline-flex items-center gap-1">sitemap.xml <ExternalLink className="h-3 w-3" /></a>
          <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="border border-zinc-800 px-2 py-1 text-zinc-300 inline-flex items-center gap-1">Google Search Console <ExternalLink className="h-3 w-3" /></a>
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <Kpi testId="kpi-pageviews" label="Pages vues" value={a.pageviews} />
        <Kpi testId="kpi-sessions" label="Visiteurs" value={a.sessions} hint={`${a.pages_per_session} pages / visite`} />
        <Kpi testId="kpi-bounce" label="Rebond" value={`${a.bounce_rate}%`} hint="1 seule page vue" />
        <Kpi testId="kpi-orders" label="Commandes" value={f.orders} hint={`taux de conversion ${f.conversion_rate}%`} />
        <Kpi testId="kpi-contacts" label="Clics WhatsApp / Snap" value={`${a.whatsapp_clicks} / ${a.snap_clicks}`} />
      </div>
      <div className="bg-ink border border-zinc-800 p-3" data-testid="audience-series">
        <p className="text-neon-green text-[11px] uppercase font-bold mb-2">Pages vues par jour</p>
        <div className="flex items-end gap-[2px] h-20">
          {a.series.map((d) => (
            <div key={d.day} title={`${d.day} : ${d.pageviews} vues · ${d.orders} cmd`} className="flex-1 bg-neon-magenta/70 hover:bg-neon-magenta" style={{ height: `${Math.max(2, (100 * d.pageviews) / maxPv)}%` }} />
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-zinc-600 mt-1"><span>{a.series[0]?.day}</span><span>{a.series[a.series.length - 1]?.day}</span></div>
      </div>
      <div className="bg-ink border border-zinc-800 p-3" data-testid="audience-funnel">
        <p className="text-neon-green text-[11px] uppercase font-bold mb-2">Parcours client (visiteurs uniques par étape)</p>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
          {steps.map(([l, v], i) => (
            <div key={l} className="text-center">
              <div className="h-16 flex items-end justify-center"><div className="w-full bg-neon-green/80" style={{ height: `${Math.max(4, (100 * v) / Math.max(steps[0][1], 1))}%` }} /></div>
              <p className="font-anton text-white text-lg leading-none mt-1">{v}</p>
              <p className="text-zinc-500 text-[10px] uppercase">{l}</p>
              {i > 0 && steps[i - 1][1] > 0 && <p className="text-zinc-600 text-[10px]">{Math.round((100 * v) / steps[i - 1][1])}%</p>}
            </div>
          ))}
        </div>
        {(f.validation_error || 0) > 0 && (
          <p data-testid="funnel-validation-errors" className="text-xs text-yellow-400 mt-3">⚠ Erreur de validation : {f.validation_error} session{f.validation_error > 1 ? "s" : ""}{errFields.length ? ` — champs : ${errFields.map((x) => `${FIELD_LABEL[x.key] || x.key} (${x.count})`).join(", ")}` : ""}</p>
        )}
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-2">
        <Bars testId="audience-pages" title="Pages les plus vues" rows={a.top_pages} total={a.pageviews} />
        <Bars testId="audience-cities" title="Villes des visiteurs" rows={a.top_cities} total={a.pageviews} />
        <Bars testId="audience-sources" title="Sources (Google, Snap, direct…)" rows={a.sources} total={a.pageviews} />
        <Bars testId="audience-devices" title="Appareils" rows={a.devices} total={a.pageviews} />
      </div>
    </div>
  );
}

export function VendorResponseTimes() {
  const [d, setD] = useState(null);
  const [days, setDays] = useState(30);
  useEffect(() => { api.get(`/admin/vendor-response-times?days=${days}`).then((r) => setD(r.data)).catch(console.error); }, [days]);
  if (!d) return null;
  const fmt = (m) => (m == null ? "—" : m < 1 ? "< 1 min" : m < 60 ? `${Math.round(m)} min` : `${(m / 60).toFixed(1)} h`);
  const slowCount = d.vendors.filter((v) => v.slow).length;
  return (
    <div className="bg-ink-surface border border-zinc-800 p-4 mb-6" data-testid="vendor-response-panel">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <h3 className="font-anton text-lg uppercase text-white flex items-center gap-2"><Timer className="h-4 w-4 text-neon-green" /> Réactivité des boutiques</h3>
        {slowCount > 0 && <span data-testid="vendor-slow-count" className="text-[10px] uppercase font-bold bg-red-500 text-white px-2 py-0.5">{slowCount} lente{slowCount > 1 ? "s" : ""}</span>}
        <div className="ml-auto flex gap-1">{[7, 30, 90].map((x) => <button key={x} onClick={() => setDays(x)} className={`text-xs font-bold px-2.5 py-1 border ${days === x ? "bg-neon-green text-black border-neon-green" : "border-zinc-700 text-zinc-400"}`}>{x} j</button>)}</div>
      </div>
      <p className="text-zinc-500 text-xs mb-3">Temps entre la commande et la 1ʳᵉ réponse (acceptée / refusée). Une boutique est signalée <span className="text-red-400 font-bold">lente</span> si sa médiane dépasse 10 min ou si une commande attend depuis plus de 15 min.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-zinc-500 text-[10px] uppercase text-left"><th className="py-1">Boutique</th><th>Cmd</th><th>Médiane</th><th>Moyenne</th><th>≤ 5 min</th><th>En attente</th><th>État</th></tr></thead>
          <tbody>
            {d.vendors.map((v) => (
              <tr key={v.vendor_id} data-testid="vendor-response-row" className={`border-t border-zinc-900 ${v.slow ? "bg-red-500/5" : ""}`}>
                <td className="py-2 text-white font-semibold">{v.name} <span className="text-zinc-600 text-xs">{v.city}</span></td>
                <td className="text-zinc-300">{v.orders}</td>
                <td className={`font-anton ${v.median_min != null && v.median_min > 10 ? "text-red-400" : "text-neon-green"}`}>{fmt(v.median_min)}</td>
                <td className="text-zinc-300">{fmt(v.avg_min)}</td>
                <td className="text-zinc-300">{v.pct_within_5min == null ? "—" : `${v.pct_within_5min}%`}</td>
                <td className={v.unanswered_now > 0 ? "text-orange-400 font-bold" : "text-zinc-500"}>{v.unanswered_now > 0 ? `${v.unanswered_now} (max ${fmt(v.oldest_wait_min)})` : "0"}</td>
                <td>{v.slow ? <span data-testid="vendor-slow-badge" className="inline-flex items-center gap-1 text-[10px] uppercase font-bold text-red-400 border border-red-400/60 px-1.5 py-0.5"><AlertTriangle className="h-3 w-3" /> Lente</span> : v.answered > 0 ? <span className="text-[10px] uppercase font-bold text-neon-green border border-neon-green/50 px-1.5 py-0.5">Réactive</span> : <span className="text-[10px] uppercase text-zinc-600">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function StockoutsPanel() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/stockouts?days=7").then((r) => setRows(r.data)).catch(console.error); }, []);
  const active = rows.filter((r) => !r.resolved);
  return (
    <div className="bg-ink-surface border border-red-500/40 p-4" data-testid="stockouts-panel">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="h-4 w-4 text-red-400" />
        <h3 className="font-anton text-lg uppercase text-white">Ruptures signalées (7 jours)</h3>
        {active.length > 0 && <span data-testid="stockouts-active-count" className="text-[10px] uppercase font-bold bg-red-500 text-white px-2 py-0.5">{active.length} en cours</span>}
      </div>
      <p className="text-zinc-500 text-xs mb-2">Chaque passage à 0 déclenche une notification push admin (active « Push mobile » en haut de page). Résolu = stock de nouveau &gt; 0.</p>
      {rows.length === 0 ? <p className="text-zinc-600 text-xs">Aucune rupture récente ✓</p> : (
        <div className="space-y-1 max-h-56 overflow-y-auto">
          {rows.map((r) => (
            <div key={r.id} data-testid="stockout-row" className="flex flex-wrap items-center gap-2 text-sm border-b border-zinc-900 py-1">
              <span className="text-white font-semibold">{r.product_name}</span>
              <span className="text-zinc-500 text-xs">{r.vendor_name} · {r.city}</span>
              <span className="text-zinc-600 text-[11px]">{new Date(r.at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
              <span className={`ml-auto text-[10px] uppercase font-bold px-1.5 py-0.5 ${r.resolved ? "text-neon-green border border-neon-green/50" : "bg-red-500 text-white"}`}>{r.resolved ? `Résolu (${r.current_quantity})` : "À réassortir"}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
